
import Vue from 'vue';
import Component from 'vue-class-component';
import { Inject } from '@/core/di/Inject';
import { TYPES } from '@/core/config/Types';
import { Prop } from 'vue-property-decorator';
import { CommodityType } from '@/freight/domain/commodity_type/CommodityType';
import { Containers } from '@/settings/domain/containers/Containers';
import { FindCommodityTypeByStatus } from '@/freight/application/commodity_type/search/FindCommodityTypesByStatus';
import { FindAllContainersByStatus } from '@/settings/application/uses_cases/containers/search/FindAllContainersByStatus';
import { WeightFindAll } from '@/settings/application/uses_cases/weight/search/WeightFindAll';
import { FindAllPackingUnitsByStatus } from '@/settings/application/uses_cases/packing_units/search/FindPackingUnitsByStatus';
import { PackingUnits } from '@/settings/domain/packing_units/PackingUnits';
import { Territories } from '@/settings/domain/views/territories/Territories';
import { SearchTerritoryByQueryDescription } from '@/settings/application/uses_cases/views/territories/SearchTerritoryByQueryDescription';
import { ReferenceFindAll } from '@/wms/application/catalogs/references/search/ReferenceFindAll';
import { References } from '@/wms/domain/catalogs/references/References';
import { WmsOrderLine } from '@/courier/domain/orderline/WmsOrderLine';
import { OrderType } from '@/courier/domain/CourierOrder/OrderTypeEnum';
import { OperationFindAll } from '@/tracking/application/uses_cases/typeoperation/search/OperationFindAll';
import { TypeOperation } from '@/settings/domain/typeOperation/TypeOperation';
import ServiceItem from '@/courier/domain/serviceItem/ServiceItem';
@Component({
  name: 'OperationRegisterForm',
  components: {}
})
export default class OperationRegisterForm extends Vue {
  @Inject(TYPES.API_VIEW_FIND_TERRITORIES_BY_QUERY)
  readonly searchTerritoriesByQueryParameter!: SearchTerritoryByQueryDescription;
  @Inject(TYPES.COMMODITY_TYPE_FIND_BY_STATUS)
  readonly searchActiveCommodities!: FindCommodityTypeByStatus;
  @Inject(TYPES.FIND_CONTAINER_BY_STATUS)
  readonly searchActiveContainers!: FindAllContainersByStatus;
  @Inject(TYPES.FINDALL_WEIGHT)
  readonly searchWeightUnits!: WeightFindAll;
  @Inject(TYPES.FIND_PACKING_UNITS_BY_STATUS)
  readonly searchActivePackingUnits!: FindAllPackingUnitsByStatus;
  @Inject(TYPES.FINDALL_REFERENCE)
  readonly findAllServiceReferences!: ReferenceFindAll;
  @Inject(TYPES.FINDALL_TYPE_OPERATION)
  readonly findTypeOperations!: OperationFindAll;

  //PROPS
  @Prop({ required: true }) form!: any;
  @Prop({ required: true }) containers!: any[];
  @Prop({ required: true }) boxes!: any[];
  @Prop({ required: true }) bulks!: any[];
  @Prop({ required: false }) readonly actions!: { save: Function; clear: Function };
  @Prop() forView!: boolean;

  //LISTAS
  containerList: Containers[] = [];
  originList: Territories[] = [];
  destinationList: Territories[] = [];
  commodityList: CommodityType[] = [];
  unitsMeasurementList: any[] = [];
  packagingList: PackingUnits[] = [];
  referencesList: any[] = [];
  typeOperationList: TypeOperation[] = [];

  //DATOS
  isLoading = false;

  //SELECTS
  selectedOrigin: Territories | null = null;
  selectedDestination: Territories | null = null;

  //VARIABLES PARA INTERVALO DE BUSQUEDA
  timeoutOrigin: any;
  timeoutDestination: any;
  searchInterval = 600;

  get typeOperationOptions() {
    return [
      {
        value: 1,
        html: `<strong>${this.$t('general.importation')}</strong><p>${this.$t('quotation.importationDescription')}</p>`
      },
      {
        value: 2,
        html: `<strong>${this.$t('general.exportation')}</strong><p>${this.$t('quotation.importationDescription')}</p>`
      },
      {
        value: 3,
        html: `<strong>${this.$t('general.localOperation')}</strong><p>${this.$t(
          'quotation.importationDescription'
        )}</p>`
      }
    ];
  }

  get containerTransportMode() {
    return [
      {
        text: `${this.$t('general.ocean')}`,
        value: 'ocean'
      },
      {
        text: `${this.$t('general.land')}`,
        value: 'land'
      }
    ];
  }

  get boxesTransportMode() {
    return [
      {
        text: `${this.$t('general.ocean')}`,
        value: 'ocean'
      },
      {
        text: `${this.$t('general.air')}`,
        value: 'air'
      },
      {
        text: `${this.$t('general.ground')}`,
        value: 'ground'
      }
    ];
  }

  get processTypeSelect() {
    if (this.form.typeOperationData == null) return 0;
    return this.form.typeOperationData.processType;
  }

  set processTypeSelect(newValue: number) {
    this.form.typeOperation = newValue;
  }

  //Ciclo de vida del componente
  async mounted() {
    await this.getCommodities();
    await this.getContainers();
    await this.getWeightUnits();
    await this.getPackingUnits();
    await this.getTypeOperation();
    !this.forView ? await this.getServiceItems() : null;
  }

  //Funcion invocada para agregar un container a la lista
  addContainer() {
    this.containers.push(new WmsOrderLine('container'));
  }
  //Funcion invocada para eliminar un container a la lista
  deleteContainer(index: number) {
    this.containers.splice(index, 1);
  }
  //Funcion invocada para agregar un caja/pallet a la lista
  addBox() {
    this.boxes.push(new WmsOrderLine('box'));
  }
  //Funcion invocada para eliminar un caja/pallet a la lista
  deleteBox(index: number) {
    this.boxes.splice(index, 1);
  }
  //Funcion invocada para agregar un bulk a la lista
  addBulk() {
    this.bulks.push(new WmsOrderLine('bulk'));
  }
  //Funcion invocada para eliminar un bulk a la lista
  deleteBulk(index: number) {
    this.bulks.splice(index, 1);
  }

  //Funcion invocada para eliminar un bulk a la lista
  deleteServiceItem(index: number) {
    this.form.additionalServices.splice(index, 1);
  }

  //Funcion invocada para buscar los territorios
  async getCities(query: string) {
    this.isLoading = true;
    const res = await this.searchTerritoriesByQueryParameter.execute(query.toUpperCase());
    this.isLoading = false;
    return res;
  }

  // Este método es llamado cuando el usuario escribe sobre el input de origen o destino para buscar territorios.
  findTerritoriesFactory(query: string, input: string, delayed = true): void {
    //Limpiamos cualquier función anterior en el timeout para evitar búsquedas repetitivas y solapadas.
    delayed ? clearTimeout(this.timeoutOrigin) : null;
    this.timeoutOrigin = setTimeout(
      async () => {
        if (query.length >= 3) {
          try {
            const res = await this.getCities(query);
            input == 'origin' ? (this.originList = res) : (this.destinationList = res);
          } catch (error) {
            this.isLoading = false;
            throw new Error(`${error}`);
          }
        }
        clearTimeout(this.timeoutOrigin);
      },
      delayed ? this.searchInterval : 0
    );
  }

  //Funcion invocada para buscar commodities activos
  async getCommodities() {
    try {
      this.isLoading = true;
      const res = await this.searchActiveCommodities.execute(true);
      res.length > 0 ? (this.commodityList = res) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar containers activos
  async getContainers() {
    try {
      this.isLoading = true;
      const res = await this.searchActiveContainers.execute(true);
      res.length > 0 ? (this.containerList = res) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar todos las unidades de peso
  async getWeightUnits() {
    try {
      this.isLoading = true;
      const res = await this.searchWeightUnits.execute();
      res.length > 0 ? (this.unitsMeasurementList = res) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar todos las unidades de peso
  async getPackingUnits() {
    try {
      this.isLoading = true;
      const res = await this.searchActivePackingUnits.execute(true);
      res.length > 0 ? (this.packagingList = res) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  // Funcion invocada para setear los valores del embalaje en la linea seleccionada
  setPackingDimensions(packing: PackingUnits, box: any) {
    box.length = packing.length;
    box.width = packing.width;
    box.height = packing.height;
  }

  //Funcion invocada para buscar los items marcados como servicio
  async getServiceItems() {
    try {
      this.isLoading = true;
      const res = await this.findAllServiceReferences.execute();
      this.referencesList = res.length > 0 ? res : [];
      this.form.additionalServices = this.referencesList
        .filter((item: References) => item.noManageStock && item.showOnQuotation)
        .map(
          (aditionalServiceReference: References): ServiceItem => {
            const serviceReference = new ServiceItem();
            serviceReference.reference = aditionalServiceReference;
            return serviceReference;
          }
        );
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para buscar los tipos de operaciones y cargar el maestro
  async getTypeOperation() {
    try {
      this.isLoading = true;
      const res = await this.findTypeOperations.execute();
      this.typeOperationList = res.length > 0 ? (res as any) : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  //Funcion invocada para generar un payload compatible
  save() {
    this.form.lines = this.form.lines.concat(this.containers, this.bulks, this.boxes);
    this.form.module = 'wms_f';
    this.form.additionalServices = this.form.additionalServices.map((item: any) => {
      return {
        ...item,
        quotationTypeLine: 'additional'
      };
    });
    this.form.typeOrder = OrderType.wmsRequest;
    this.actions.save();
    this.actions.clear();
  }
}
